import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import type { DateRange } from "../utils/date";
import { DashboardService } from "../../../../services/dashboard/dashboardService";

import { LowStockAlertCard, BackendItemLowStock } from "../../components/products/LowStockAlertCard";
import {
    StockMovementsPerProductCard,
    BackendItemMovements,
} from "../../components/products/StockMovementsPerProductCard";
import {
    DeliveredToSuppliersRanking,
    BackendItemDelivered,
} from "../../components/products/DeliveredToSuppliersRanking";

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};

interface Props {
    range: DateRange;
    refreshKey: number;
}

export const ProductsStatsTab: React.FC<Props> = ({ range, refreshKey }) => {
    // === Alertas de bajo stock ===
    const [lowStockItems, setLowStockItems] = useState<BackendItemLowStock[]>([]);
    const [loadingLowStock, setLoadingLowStock] = useState(false);
    const [errorLowStock, setErrorLowStock] = useState<string | null>(null);

    // === Movimientos por producto ===
    const [movementsItems, setMovementsItems] = useState<BackendItemMovements[]>([]);
    const [loadingMovements, setLoadingMovements] = useState(false);
    const [errorMovements, setErrorMovements] = useState<string | null>(null);

    // === Entregas a proveedores (ranking) ===
    const [deliveredItems, setDeliveredItems] = useState<BackendItemDelivered[]>([]);
    const [loadingDelivered, setLoadingDelivered] = useState(false);
    const [errorDelivered, setErrorDelivered] = useState<string | null>(null);

    // === Fetchers ===
    const fetchLowStock = async () => {
        try {
            setLoadingLowStock(true);
            setErrorLowStock(null);
            const service = new DashboardService();
            // Si tu endpoint no usa rango, quita el argumento:
            const res = await service.productsWithMinimumStock();
            const body = (res as any)?.response?.data;
            if (body?.success) setLowStockItems(body.data ?? []);
            else setLowStockItems([]);
        } catch (e: any) {
            setErrorLowStock(e?.message || "Error obteniendo productos con bajo stock");
            setLowStockItems([]);
        } finally {
            setLoadingLowStock(false);
        }
    };

    const fetchMovements = async (r: DateRange) => {
        try {
            setLoadingMovements(true);
            setErrorMovements(null);
            const service = new DashboardService();
            const res = await service.stockMovementsPerProduct({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setMovementsItems(body.data ?? []);
            else setMovementsItems([]);
        } catch (e: any) {
            setErrorMovements(e?.message || "Error obteniendo movimientos de stock");
            setMovementsItems([]);
        } finally {
            setLoadingMovements(false);
        }
    };

    const fetchDelivered = async (r: DateRange) => {
        try {
            setLoadingDelivered(true);
            setErrorDelivered(null);
            const service = new DashboardService();
            const res = await service.productQuantityDeliveredToProviders({ time: { start: r.start, end: r.end } });
            const body = (res as any)?.response?.data;
            if (body?.success) setDeliveredItems(body.data ?? []);
            else setDeliveredItems([]);
        } catch (e: any) {
            setErrorDelivered(e?.message || "Error obteniendo entregas a proveedores");
            setDeliveredItems([]);
        } finally {
            setLoadingDelivered(false);
        }
    };

    const fetchBundle = (r: DateRange) => {
        fetchLowStock();
        fetchMovements(r);
        fetchDelivered(r);
    };

    useEffect(() => {
        fetchBundle(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.start, range.end, refreshKey]);

    return (
        <>
            {/* Alertas */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <LowStockAlertCard
                    items={lowStockItems}
                    loading={loadingLowStock}
                    error={errorLowStock}
                    className="h-full"
                    height={160}
                />
            </motion.div>

            {/* Movimientos (Entradas/Salidas) */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <StockMovementsPerProductCard
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={movementsItems}
                    loading={loadingMovements}
                    error={errorMovements}
                    height={320}
                    className="h-full"
                    maxRows={6}
                />
            </motion.div>

            {/* Entregas a proveedores */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="h-full">
                <DeliveredToSuppliersRanking
                    periodLabel={`Periodo: ${range.start} → ${range.end}`}
                    items={deliveredItems}
                    loading={loadingDelivered}
                    error={errorDelivered}
                    height={320}
                    className="h-full"
                    color="#6366f1"
                    maxRows={6}
                />
            </motion.div>
        </>
    );
};
